/**
 * Platform roles — the scope's User Roles (Actors), as role keys carried on the
 * session user in `platformRoleKeys`.
 *
 * Kept out of RoleRoute.jsx so both components and plain modules can import them
 * without tripping react-refresh's component-only-exports rule.
 */

export const PLATFORM_ROLE_KEYS = {
  PLATFORM_ADMINISTRATOR: 'platform_administrator',
  MODEL_ADMINISTRATOR: 'model_administrator',
  WORKFLOW_DEVELOPER: 'workflow_developer',
  APPROVER: 'approver',
  CREATIVE_USER: 'creative_user',
  AUDITOR: 'auditor',
};

/** Display labels, matching the scope's User Role names exactly. */
export const PLATFORM_ROLE_LABELS = {
  [PLATFORM_ROLE_KEYS.PLATFORM_ADMINISTRATOR]: 'Platform Administrator',
  [PLATFORM_ROLE_KEYS.MODEL_ADMINISTRATOR]: 'Model Administrator',
  [PLATFORM_ROLE_KEYS.WORKFLOW_DEVELOPER]: 'Workflow Developer',
  [PLATFORM_ROLE_KEYS.APPROVER]: 'Senior Creative / Approver',
  [PLATFORM_ROLE_KEYS.CREATIVE_USER]: 'Creative User',
  [PLATFORM_ROLE_KEYS.AUDITOR]: 'Auditor / Compliance',
};

/**
 * The role keys held by a session user.
 *
 * The real source is `user.platformRoleKeys`, populated by the identity backend.
 * The scaffold's user table only ships the platform-level `role` column
 * (`admin` / `user`), so until a user has been assigned real platform roles we
 * fall back to mapping `role === 'admin'` onto Platform Administrator. That
 * fallback is deliberately narrow: it never grants any *other* business role, so
 * a plain `user` is denied every gated page rather than silently let through.
 */
export function roleKeysOf(user) {
  if (!user) return [];
  if (Array.isArray(user.platformRoleKeys) && user.platformRoleKeys.length) {
    return user.platformRoleKeys;
  }
  return user.role === 'admin' ? [PLATFORM_ROLE_KEYS.PLATFORM_ADMINISTRATOR] : [];
}

/** True when the user holds at least one of `allowed` (empty `allowed` = any). */
export function hasAnyRole(user, allowed) {
  if (!allowed || allowed.length === 0) return true;
  const held = roleKeysOf(user);
  return allowed.some((key) => held.includes(key));
}

/** Workspace roles that may change a workspace's content (the API's WRITER_ROLES). */
export const WORKSPACE_WRITER_ROLES = ['Owner', 'Editor'];

/**
 * True when the user holds one of the platform `roles` and, unless a platform administrator, is Owner or Editor in `workspaceId`
 * (the API checks the same, in the resource's own workspace). With no workspace id or no workspace list to check against, only
 * the platform role is known, so the controls show and the API decides.
 */
export function canWriteInWorkspace(user, workspaceId, roles) {
  if (!hasAnyRole(user, roles)) return false;
  if (hasAnyRole(user, [PLATFORM_ROLE_KEYS.PLATFORM_ADMINISTRATOR])) return true;
  if (!workspaceId || !Array.isArray(user?.workspaces)) return true;
  return WORKSPACE_WRITER_ROLES.includes(user.workspaces.find((w) => w.workspaceId === workspaceId)?.role);
}
